import { useCallback, useEffect, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";

import { Disclose, Headline, Loading, Refusal, Tooltip } from "@lopie/kernel-ui";

/**
 * What are they costing, and for what?
 *
 * ## What the first version got wrong
 *
 * It listed the same two agents twice — once under *what each one costs* and
 * again under *where the money goes* — with a paragraph of reasoning above
 * each list. Two parallel tables of the same entities, six numbers, and three
 * explanations to read before reaching any of them. The screen's own rules
 * say one figure at the top of the scale and the detail one click in, and it
 * broke both.
 *
 * It also drew the read/write ratio as a proportion bar, which is the wrong
 * shape for the data: at 254:1 and 65:1 both bars render as a solid block.
 * A shape that cannot tell the two apart is worse than the number, because it
 * looks like information. The ratio is a number now.
 *
 * ## One row per agent, and the row is the whole answer
 *
 * Money out, work done, and how hard it had to read to do it. Everything
 * about one agent is on one line, so the comparison is vertical and needs no
 * scrolling between two lists.
 *
 * ## The headline is the finding, not the total
 *
 * `$20.19 across 8 runs` is true and answers nothing — a total is what you
 * check, not what you act on. The number worth the top of the scale is the
 * money that bought nothing, and when there is none of that the total takes
 * its place.
 */

const count = (n) => Number(n || 0).toLocaleString("en-US");

function dollars(usd, places = 2) {
  const n = Number(usd) || 0;
  return `$${n.toLocaleString("en-US", {
    minimumFractionDigits: places, maximumFractionDigits: places,
  })}`;
}

/** Rows to ask the log for, per agent. */
const ROWS = 200;
const MAX_AGENTS = 12;

/** One agent, one line: spent, changed, and how much it reads to do it. */
function Row({ agent }) {
  const spend = agent.spend || {};
  const usd = Number(spend.usd) || 0;
  const writes = agent.writes;
  const idle = writes === 0 && usd > 0;
  const input = Number(spend.input_total) || 0;
  const output = Number(spend.output) || 0;
  const ratio = output ? Math.round(input / output) : null;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr auto",
          sm: "minmax(0, 1fr) 6rem 9rem 9rem",
        },
        gap: { xs: 0.5, sm: 2 },
        alignItems: "baseline",
        py: 1.5,
        borderTop: 1,
        borderColor: "divider",
        minWidth: 0,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>
          {agent.label || agent.name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {count(spend.runs || 0)} {spend.runs === 1 ? "run" : "runs"}
        </Typography>
      </Box>

      <Typography
        variant="body2"
        sx={{ fontVariantNumeric: "tabular-nums", textAlign: { sm: "right" } }}
      >
        {dollars(usd)}
      </Typography>

      <Tooltip
        title={
          agent.stoppedEarly
            ? "A floor. The read looking for this agent's rows stopped before it finished, so there may be more."
            : "Rows in the audit log naming this agent. The kernel writes them, not the agent."
        }
      >
        <Typography
          variant="body2"
          sx={{
            cursor: "help",
            color: idle ? "kernel.attention" : "text.secondary",
            fontWeight: idle ? 600 : 400,
          }}
        >
          {writes === null
            ? "—"
            : idle
              ? "changed nothing"
              : `${count(writes)} ${writes === 1 ? "change" : "changes"}`}
        </Typography>
      </Tooltip>

      {/* A number, not a bar. A proportion bar cannot tell 254:1 from 65:1 —
          both are a solid block — and a shape that hides the difference is
          worse than the figure it replaced. */}
      <Tooltip
        title={`${count(input)} tokens read, ${count(output)} written. Output bills at several times the rate of input, so a high ratio is an agent paying to take in a lot to say very little.`}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ cursor: "help", fontVariantNumeric: "tabular-nums" }}
        >
          {ratio ? `${count(ratio)}:1 read` : "—"}
        </Typography>
      </Tooltip>
    </Box>
  );
}

export default function Spending({ get, onEffective }) {
  const [agents, setAgents] = useState(null);
  const [failure, setFailure] = useState(null);

  const load = useCallback(async () => {
    try {
      const body = await get("/agents");
      const roster = (body.agents || []).slice(0, MAX_AGENTS);
      const counted = await Promise.all(
        roster.map((a) =>
          get("/audit", { agent: a.name, limit: ROWS, sort: "desc" })
            .then((page) => ({
              ...a,
              writes: (page.items || []).length,
              stoppedEarly: Boolean(page.stopped_early),
            }))
            .catch(() => ({ ...a, writes: null, stoppedEarly: false })),
        ),
      );
      setAgents(counted);
      setFailure(null);
    } catch (e) {
      setFailure(e);
      setAgents(null);
    }
  }, [get]);

  useEffect(() => {
    load();
    onEffective?.(null);
  }, [load, onEffective]);

  if (failure) {
    return <Refusal failure={failure} actor="Agent OS" onRetry={load} />;
  }
  if (!agents) return <Loading label="Pricing every agent's runs" />;

  const total = agents.reduce((n, a) => n + (Number(a.spend?.usd) || 0), 0);
  const runs = agents.reduce((n, a) => n + (Number(a.spend?.runs) || 0), 0);
  const idle = agents.filter((a) => (Number(a.spend?.usd) || 0) > 0 && a.writes === 0);
  const wasted = idle.reduce((n, a) => n + (Number(a.spend?.usd) || 0), 0);
  const uncapped = agents.filter((a) => !a.budget).length;

  return (
    <Stack spacing={3} sx={{ minWidth: 0 }}>
      <Headline
        eyebrow={wasted ? "Spent with nothing to show for it" : "Spent by every agent, ever"}
        figure={dollars(wasted || total)}
        tone={wasted ? "attention" : undefined}
        caption={
          wasted
            ? `by ${idle.length} of ${agents.length} agents`
            : `across ${count(runs)} ${runs === 1 ? "run" : "runs"}`
        }
        basis={
          "Computed from what each run reported at Anthropic's published "
          + "prices, not read from a bill. A change count is what one read of "
          + "the log found, so it is a floor wherever that read stopped early."
        }
        lede={
          wasted
            ? `${dollars(total)} has gone through these agents in total, and ${dollars(wasted)} of it bought no change the log can see.`
            : `${dollars(total)} across ${count(runs)} runs, and every agent that spent also wrote.`
        }
      />

      <Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr auto",
              sm: "minmax(0, 1fr) 6rem 9rem 9rem",
            },
            gap: { xs: 0.5, sm: 2 },
            pb: 0.5,
          }}
        >
          <Typography variant="overline" color="text.secondary">
            Agent
          </Typography>
          <Typography variant="overline" color="text.secondary" sx={{ textAlign: { sm: "right" } }}>
            Cost
          </Typography>
          <Typography variant="overline" color="text.secondary">
            Did what
          </Typography>
          <Typography variant="overline" color="text.secondary">
            Per word out
          </Typography>
        </Box>
        {agents.map((a) => (
          <Row key={`${a.app_type}:${a.name}`} agent={a} />
        ))}
      </Box>

      <Disclose label="How to read this">
        <Stack spacing={1.5} sx={{ maxWidth: "62ch" }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Cost against change is the division nothing else
            performs.</strong> A model provider knows what a session cost and
            cannot know whether anything changed as a result — those writes
            went through a kernel it has never seen. Both halves are here, so
            an agent that spends and changes nothing is visible rather than
            inferred.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>&ldquo;Per word out&rdquo;</strong> is tokens read for
            every one written. Output bills at several times the rate of input,
            so a high number is an agent paying to take in a great deal to say
            very little — a prompt and caching question, and one that is
            invisible in a dollar total.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Changed nothing may be correct.</strong> An agent that only
            reports has nothing to write, and the kernel refuses every write
            verb it was never granted. It is worth knowing either way.
          </Typography>
          {uncapped ? (
            <Typography variant="body2" sx={{ color: "kernel.attention" }}>
              {uncapped} of {agents.length}{" "}
              {uncapped === 1 ? "agent has" : "agents have"} no spending limit,
              so nothing stops them but somebody noticing. A limit is two
              numbers on an agent&apos;s Settings panel.
            </Typography>
          ) : null}
        </Stack>
      </Disclose>
    </Stack>
  );
}
